import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from './usePantryName';
import Feather from '@expo/vector-icons/Feather';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { Paprika } from '../../styles/theme';
import type { InventoryItem } from '../../redux/inventorySlice';
import { planDuplicateMerge } from '../../utils/pantryDuplicates';
import { PantryLocation } from '../../utils/pantryLocation';
import { ZONE_KEYS, ZONE_META } from './zoneMeta';
import { FoodTile } from './FoodTile';

/** What the user decided. Every field is a lesson the matcher will keep. */
export interface TidyDecisions {
  /** Groups to collapse. Carries the rows themselves, not just ids, because
   *  the merge deletes the absorbed ones and their names are needed to learn. */
  merge: InventoryItem[][];
  /** Groups the user said are genuinely different foods. */
  separate: InventoryItem[][];
  /** Where the user put a row that had nowhere to be. */
  placements: Array<{ item: InventoryItem; zone: PantryLocation }>;
}

interface DedupeContentProps {
  /** Groups we are confident about, from `selectCertainDuplicateGroups`. */
  certain: InventoryItem[][];
  /** Groups that merely look alike, from `selectMergeCandidates`. */
  candidates: InventoryItem[][];
  /** Rows whose zone was never determined, from `selectUnsortedItems`. */
  unsorted: InventoryItem[];
  onConfirm: (decisions: TidyDecisions) => void;
  onCancel: () => void;
}

type CandidateAnswer = 'same' | 'different';

/**
 * Tidy the pantry: merge duplicates, settle look-alikes, place strays.
 *
 * Nothing happens until the user confirms, because merging deletes rows both
 * locally and in Firestore, and two rows that look alike are occasionally two
 * real, separate things.
 *
 * Every answer here is recorded, including the negative ones. A rejected merge
 * is the most valuable signal in the screen — it is the only way the matcher
 * learns that "vin blanc" and "vin rouge" are not the same bottle — so
 * look-alikes get an explicit "same"/"different" pair rather than a checkbox,
 * where leaving it alone would be indistinguishable from saying no.
 */
export function DedupeContent({
  certain,
  candidates,
  unsorted,
  onConfirm,
  onCancel,
}: DedupeContentProps) {
  const { t } = useTranslation();
  const pantryName = usePantryName();

  const plans = certain.map(planDuplicateMerge);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [answers, setAnswers] = useState<Record<number, CandidateAnswer>>({});
  const [placements, setPlacements] = useState<Record<string, PantryLocation>>({});

  const toggleCertain = (survivorId: string) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(survivorId)) next.delete(survivorId);
      else next.add(survivorId);
      return next;
    });
  };

  const answer = (index: number, value: CandidateAnswer) => {
    setAnswers(prev => ({ ...prev, [index]: prev[index] === value ? undefined as never : value }));
  };

  const place = (itemId: string, zone: PantryLocation) => {
    setPlacements(prev => ({ ...prev, [itemId]: prev[itemId] === zone ? undefined as never : zone }));
  };

  const acceptedPlans = plans.filter(plan => !excluded.has(plan.survivor.id));
  const acceptedCandidates = candidates.filter((_, i) => answers[i] === 'same');
  const rejectedCandidates = candidates.filter((_, i) => answers[i] === 'different');
  const rejectedCertain = plans.filter(plan => excluded.has(plan.survivor.id));
  const chosenPlacements = Object.entries(placements).filter(([, zone]) => !!zone);

  const decisionCount =
    acceptedPlans.length +
    acceptedCandidates.length +
    rejectedCandidates.length +
    rejectedCertain.length +
    chosenPlacements.length;

  const handleConfirm = () => {
    if (decisionCount === 0) return;
    onConfirm({
      merge: [
        ...acceptedPlans.map(plan => [plan.survivor, ...plan.absorbed]),
        ...acceptedCandidates,
      ],
      separate: [...rejectedCertain.map(plan => [plan.survivor, ...plan.absorbed]), ...rejectedCandidates],
      placements: chosenPlacements.map(([itemId, zone]) => ({
        item: unsorted.find(i => i.id === itemId)!,
        zone,
      })).filter(p => !!p.item),
    });
  };

  const nothingToDo = plans.length === 0 && candidates.length === 0 && unsorted.length === 0;

  if (nothingToDo) {
    return (
      <View style={styles.emptyState}>
        <Feather name="check-circle" size={28} color={Paprika.textMuted} />
        <Text style={styles.emptyText}>{t('pantry.tidy.none')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
        {plans.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pantry.tidy.certainTitle')}</Text>
            <Text style={styles.sectionIntro}>{t('pantry.tidy.certainIntro')}</Text>

            {plans.map(plan => {
              const isOn = !excluded.has(plan.survivor.id);
              // The merged row may adopt a clearer name than the row that survives.
              const resultName = pantryName({ ...plan.survivor, name: plan.updates.name ?? plan.survivor.name });
              const absorbedNames = [
                ...plan.absorbed.map(pantryName),
                ...(plan.updates.name ? [pantryName(plan.survivor)] : []),
              ];

              return (
                <Pressable
                  key={plan.survivor.id}
                  onPress={() => toggleCertain(plan.survivor.id)}
                  style={[styles.row, !isOn && styles.rowOff]}
                >
                  <View style={[styles.checkbox, isOn && styles.checkboxChecked]}>
                    {isOn && <Feather name="check" size={14} color={Paprika.onPrimary} />}
                  </View>

                  <FoodTile category={plan.survivor.category} name={resultName} size={36} radius={10} />

                  <View style={styles.rowInfo}>
                    <Text style={styles.rowName} numberOfLines={1}>{resultName}</Text>
                    <Text style={styles.rowSub} numberOfLines={2}>
                      {/* '·' rather than ',' — the names themselves contain commas
                          ("nectarines, tranchées") and a comma list reads as one blur. */}
                      {t('pantry.tidy.absorbs', { names: absorbedNames.join(' · ') })}
                    </Text>
                  </View>
                </Pressable>
              );
            })}

            <Text style={styles.footnote}>{t('pantry.tidy.expiryNote')}</Text>
          </View>
        )}

        {candidates.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pantry.tidy.maybeTitle')}</Text>
            <Text style={styles.sectionIntro}>{t('pantry.tidy.maybeIntro')}</Text>

            {candidates.map((group, index) => (
              <View key={group.map(i => i.id).join('-')} style={styles.row}>
                <FoodTile category={group[0].category} name={group[0].name} size={36} radius={10} />

                <View style={styles.rowInfo}>
                  <Text style={styles.rowName} numberOfLines={2}>
                    {group.map(pantryName).join(' · ')}
                  </Text>
                </View>

                <View style={styles.answerGroup}>
                  <Pressable
                    onPress={() => answer(index, 'same')}
                    style={[styles.answerChip, answers[index] === 'same' && styles.answerChipOn]}
                    accessibilityLabel={t('pantry.tidy.same')}
                  >
                    <Feather
                      name="check"
                      size={15}
                      color={answers[index] === 'same' ? Paprika.onPrimary : Paprika.textSecondary}
                    />
                  </Pressable>
                  <Pressable
                    onPress={() => answer(index, 'different')}
                    style={[styles.answerChip, answers[index] === 'different' && styles.answerChipOff]}
                    accessibilityLabel={t('pantry.tidy.different')}
                  >
                    <Feather
                      name="x"
                      size={15}
                      color={answers[index] === 'different' ? Paprika.onPrimary : Paprika.textSecondary}
                    />
                  </Pressable>
                </View>
              </View>
            ))}
          </View>
        )}

        {unsorted.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pantry.tidy.sortTitle')}</Text>
            <Text style={styles.sectionIntro}>{t('pantry.tidy.sortIntro')}</Text>

            {unsorted.map(item => (
              <View key={item.id} style={styles.sortRow}>
                <View style={styles.sortHeader}>
                  <FoodTile category={item.category} name={item.name} size={36} radius={10} />
                  <Text style={styles.rowName} numberOfLines={2}>{pantryName(item)}</Text>
                </View>

                <View style={styles.zoneRow}>
                  {ZONE_KEYS.map(zone => {
                    const active = placements[item.id] === zone;
                    return (
                      <Pressable
                        key={zone}
                        onPress={() => place(item.id, zone)}
                        style={[styles.zoneChip, active && styles.zoneChipOn]}
                      >
                        <MaterialCommunityIcons
                          name={ZONE_META[zone].icon.name as never}
                          size={14}
                          color={active ? Paprika.onPrimary : Paprika.textSecondary}
                        />
                        <Text style={[styles.zoneLabel, active && styles.zoneLabelOn]} numberOfLines={1}>
                          {t(ZONE_META[zone].labelKey)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <Pressable style={styles.cancelButton} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>{t('pantry.tidy.cancel')}</Text>
        </Pressable>
        <Pressable
          style={[styles.confirmButton, decisionCount === 0 && styles.confirmButtonDisabled]}
          onPress={handleConfirm}
          disabled={decisionCount === 0}
        >
          <Text style={styles.confirmButtonText} numberOfLines={1}>
            {t(`pantry.tidy.confirm_${decisionCount === 1 ? 'one' : 'other'}`, {
              count: decisionCount,
            })}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 24,
  },
  emptyText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 24,
  },
  section: {
    gap: 8,
  },
  sectionTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 19,
    color: Paprika.textPrimary,
  },
  sectionIntro: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    lineHeight: 18,
    color: Paprika.textSecondary,
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
  },
  rowOff: {
    opacity: 0.42,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 7,
    borderWidth: 1.5,
    borderColor: Paprika.hairline,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: Paprika.primary,
    borderColor: Paprika.primary,
  },
  rowInfo: {
    flex: 1,
    minWidth: 0,
  },
  rowName: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    fontWeight: '500',
    color: Paprika.textPrimary,
    flexShrink: 1,
  },
  rowSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    lineHeight: 16,
    color: Paprika.textMuted,
    marginTop: 2,
  },
  answerGroup: {
    flexDirection: 'row',
    gap: 6,
    flexShrink: 0,
  },
  answerChip: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.surfaceLow,
  },
  answerChipOn: {
    backgroundColor: Paprika.primary,
  },
  answerChipOff: {
    backgroundColor: Paprika.textMuted,
  },
  sortRow: {
    gap: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
  },
  sortHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  zoneRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  zoneChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 10,
    backgroundColor: Paprika.surfaceLow,
  },
  zoneChipOn: {
    backgroundColor: Paprika.primary,
  },
  zoneLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    fontWeight: '500',
    color: Paprika.textSecondary,
  },
  zoneLabelOn: {
    color: Paprika.onPrimary,
  },
  footnote: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    lineHeight: 16,
    color: Paprika.textMuted,
    marginTop: 4,
  },
  footer: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: Paprika.cardBg,
  },
  cancelButton: {
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLow,
  },
  cancelButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textSecondary,
  },
  confirmButton: {
    flex: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderRadius: 13,
    backgroundColor: Paprika.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmButtonDisabled: {
    opacity: 0.4,
  },
  confirmButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.onPrimary,
  },
});
